import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { NAV_LABELS } from '@/lib/nav-labels.js';
import { podeVer, useMe } from '@/lib/me.js';

type Resumo = {
  diagnosticos: {
    total: number;
    empresas: number;
    ultimo: {
      id: string;
      title: string | null;
      status: string;
      empresa_nome: string;
      updated_at: string;
    } | null;
  };
  trilhas_em_andamento: {
    programa_id: string;
    programa_titulo: string;
    trilha_slug: string;
    trilha_titulo: string;
    trilha_tipo: string;
    progresso_pct: number;
    aulas_concluidas: number;
    aulas_com_progresso: number;
  }[];
  eventos: {
    id: string;
    titulo: string;
    inicio_em: string | null;
    minha_participacao: string | null;
  }[];
  certificados_recentes: { id: string; titulo: string; codigo: string; emitido_em: string }[];
};

const catalogUrl =
  import.meta.env.VITE_COMMERCIAL_OS_MARKETING_URL ?? 'https://os.hack.net.br/catalogo-saas';

function Widget({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="rounded-card border border-ha-border2 bg-ha-card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ha-muted">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function PainelPage() {
  const me = useMe();
  const q = useQuery({
    queryKey: ['dashboard', 'resumo'],
    queryFn: async () => (await api.get<Resumo>('/dashboard/resumo')).data,
  });

  if (q.isLoading) {
    return <PageLayout title="Início">Carregando…</PageLayout>;
  }
  if (q.isError || !q.data) {
    return (
      <PageLayout title="Início">
        <p className="text-feedback">Não foi possível carregar o painel.</p>
      </PageLayout>
    );
  }

  const d = q.data;

  return (
    <PageLayout
      title="Início"
      description="Visão geral da sua jornada no Hack Academy."
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <Widget
          title="Diagnósticos (empresas)"
          action={
            <Link to="/metodo/diagnostico" className="text-xs text-accent underline">
              Abrir
            </Link>
          }
        >
          <p className="text-2xl font-bold text-ha-text">{d.diagnosticos.total}</p>
          <p className="text-sm text-ha-muted">
            {d.diagnosticos.empresas} empresa(s) vinculada(s)
          </p>
          {d.diagnosticos.ultimo ? (
            <p className="mt-2 text-sm text-ha-subtle">
              Último: {d.diagnosticos.ultimo.title || 'Diagnóstico'} — {d.diagnosticos.ultimo.empresa_nome} (
              {d.diagnosticos.ultimo.status})
            </p>
          ) : (
            <p className="mt-2 text-sm text-ha-subtle">Nenhum diagnóstico ainda.</p>
          )}
        </Widget>

        <Widget
          title="Trilhas em andamento"
          action={
            <Link to="/conhecimento/jornadas" className="text-xs text-accent underline">
              Jornadas
            </Link>
          }
        >
          {d.trilhas_em_andamento.length === 0 ? (
            <p className="text-sm text-ha-subtle">Comece uma jornada ou micro-learning.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {d.trilhas_em_andamento.map((t) => (
                <li key={t.programa_id}>
                  <Link
                    to={`/aprendizado/trilha/${t.trilha_slug}`}
                    className="text-sm font-medium text-accent underline"
                  >
                    {t.programa_titulo}
                  </Link>
                  <p className="text-xs text-ha-muted">
                    {t.trilha_titulo} · {t.progresso_pct}% · {t.aulas_concluidas} aula(s) concluída(s)
                  </p>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ha-border2">
                    <div
                      className="h-full bg-accent"
                      style={{ width: `${Math.min(100, t.progresso_pct)}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Widget>

        <Widget
          title="Próximos eventos"
          action={
            <Link to="/eventos" className="text-xs text-accent underline">
              Ver todos
            </Link>
          }
        >
          {d.eventos.length === 0 ? (
            <p className="text-sm text-ha-subtle">Nenhum evento na agenda.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {d.eventos.map((e) => (
                <li key={e.id} className="text-sm">
                  <span className="font-medium text-ha-text">{e.titulo}</span>
                  {e.inicio_em ? (
                    <span className="ml-2 text-xs text-ha-muted">
                      {new Date(e.inicio_em).toLocaleString('pt-BR')}
                    </span>
                  ) : null}
                  {e.minha_participacao === 'CONFIRMADO' && (
                    <span className="ml-2 text-xs font-semibold text-emerald-600">Confirmado</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Widget>

        {podeVer(me.data, 'conselheiro') ? (
          <Widget
            title={NAV_LABELS.conselheiro}
            action={
              <Link to="/direcao/conselheiro" className="text-xs text-accent underline">
                Abrir
              </Link>
            }
          >
            <p className="text-sm text-ha-subtle">Conselheiro, ciclo e pessoas da sua empresa no programa.</p>
          </Widget>
        ) : null}

        <Widget title="Atalhos">
          <ul className="flex flex-wrap gap-2 text-sm">
            <li>
              <Link to="/metodo/biblioteca" className="text-accent underline">
                {NAV_LABELS.biblioteca5Forcas}
              </Link>
            </li>
            {podeVer(me.data, 'ia') ? (
              <li>
                <Link to="/tecnologia/hack-ia" className="text-accent underline">
                  {NAV_LABELS.hackIa}
                </Link>
              </li>
            ) : null}
            <li>
              <Link to="/conexao/ecossistema" className="text-accent underline">
                {NAV_LABELS.solucoesHack}
              </Link>
            </li>
            <li>
              <Link to="/conhecimento/micro" className="text-accent underline">
                {NAV_LABELS.microLearning}
              </Link>
            </li>
          </ul>
        </Widget>

        {d.certificados_recentes.length > 0 && (
          <Widget title="Certificados recentes">
            <ul className="flex flex-col gap-1 text-sm">
              {d.certificados_recentes.map((c) => (
                <li key={c.id}>
                  <Link to={`/certificado/${c.codigo}`} className="text-accent underline">
                    {c.titulo}
                  </Link>
                </li>
              ))}
            </ul>
          </Widget>
        )}
      </div>
    </PageLayout>
  );
}
